import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ClientLayout } from './components/layout/ClientLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { useAuthStore } from './stores/auth-store';
import type { UserRole } from './types';

// Public pages
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { NotFoundPage } from './pages/NotFoundPage';

// Client pages
import { HomePage } from './pages/client/HomePage';
import { TicketsPage } from './pages/client/TicketsPage';
import { TicketDetailPage } from './pages/client/TicketDetailPage';
import { NewTicketPage } from './pages/client/NewTicketPage';
import { ProfilePage } from './pages/client/ProfilePage';

// Admin pages
import { DashboardPage } from './pages/admin/DashboardPage';
import { AdminTicketsPage } from './pages/admin/TicketsPage';
import { AdminTicketDetailPage } from './pages/admin/TicketDetailPage';
import { ClientsPage } from './pages/admin/ClientsPage';
import { ClientDetailPage } from './pages/admin/ClientDetailPage';
import { UsersPage } from './pages/admin/UsersPage';
import { SlaPage } from './pages/admin/SlaPage';
import { TagsPage } from './pages/admin/TagsPage';
import { WebConPage } from './pages/admin/WebConPage';
import { MicrosoftPage } from './pages/admin/MicrosoftPage';
import { AuditPage } from './pages/admin/AuditPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { ActivityDashboardPage } from './pages/admin/ActivityDashboardPage';
import { AdminNewTicketPage } from './pages/admin/NewTicketPage';
import { FeatureRequestsPage } from './pages/admin/FeatureRequestsPage';
import { FeatureRequestDetailPage } from './pages/admin/FeatureRequestDetailPage';
import { FeatureRequestChatPage } from './pages/admin/FeatureRequestChatPage';
import { NewFeatureRequestPage } from './pages/admin/NewFeatureRequestPage';

export function App() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const logout = useAuthStore((s) => s.logout);
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch('/api/auth/me', { credentials: 'include' })
      .then(async (response) => {
        if (response.status === 401) return null;
        const payload = (await response.json()) as {
          success?: boolean;
          data?: {
            id: string;
            email: string;
            name: string;
            role: UserRole;
            clientElementId?: number;
            isSuperAdmin?: boolean;
          };
        };
        if (!response.ok || !payload.success) return null;
        return payload.data ?? null;
      })
      .then((user) => {
        if (cancelled) return;
        if (user) {
          setAuth(user);
          return;
        }
        logout();
      })
      .catch(() => {
        if (!cancelled) logout();
      })
      .finally(() => {
        if (!cancelled) setSessionChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [setAuth, logout]);

  if (!sessionChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-gray-500">Se verifică sesiunea...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Client portal */}
      <Route element={<ProtectedRoute allowedRoles={['CLIENT']} />}>
        <Route element={<ClientLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/new" element={<NewTicketPage />} />
          <Route path="/tickets/:id" element={<TicketDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>
      </Route>

      {/* Admin / Agent portal */}
      <Route element={<ProtectedRoute allowedRoles={['AGENT', 'ADMIN']} />}>
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<DashboardPage />} />
          <Route path="/admin/tickets" element={<AdminTicketsPage />} />
          <Route path="/admin/tickets/new" element={<AdminNewTicketPage />} />
          <Route path="/admin/tickets/:id" element={<AdminTicketDetailPage />} />
          <Route path="/admin/clients" element={<ClientsPage />} />
          <Route path="/admin/clients/:id" element={<ClientDetailPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/sla" element={<SlaPage />} />
          <Route path="/admin/tags" element={<TagsPage />} />
          <Route path="/admin/webcon" element={<WebConPage />} />
          <Route path="/admin/microsoft" element={<MicrosoftPage />} />
          <Route path="/admin/audit" element={<AuditPage />} />
          <Route path="/admin/settings" element={<SettingsPage />} />
          <Route path="/admin/activity" element={<ActivityDashboardPage />} />
          <Route path="/admin/feature-requests" element={<FeatureRequestsPage />} />
          <Route path="/admin/feature-requests/new" element={<NewFeatureRequestPage />} />
          <Route path="/admin/feature-requests/:id" element={<FeatureRequestDetailPage />} />
          <Route path="/admin/feature-requests/:id/chat" element={<FeatureRequestChatPage />} />
        </Route>
      </Route>

      {/* Catch-all */}
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
